/**
 * Hospitals domain — utility functions and shared types.
 *
 * Re-exports generated entity types plus the typed adapters that bridge the
 * Kubb-generated shapes to the legacy props expected by `DECommonTable`,
 * `DemographicsSection`, etc. The cross-domain adapters live here (rather
 * than in component files) so the casts are documented and centralized.
 */

import type { HospitalChainProxy } from '../../models/hospital'
import type {
  StandardPaginatedResponse,
  StandardSummaryResponse,
} from '../../models/paginated-response'
import type { AddressInfo } from '../../models/restaurant'
import { getHospitalsSummary as getHospitalsSummaryClient } from '../generated/clients/getHospitalsSummary'
import { listHospitals as listHospitalsClient } from '../generated/clients/listHospitals'
import type { GetHospitalsSummaryQueryParams } from '../generated/types/GetHospitalsSummary'
import type { HospitalNested } from '../generated/types/HospitalNested'
import type { ListHospitalsQueryParams } from '../generated/types/ListHospitals'

// ── Domain entity type re-exports ───────────────────────────────────

export type { HospitalListItem } from '../generated/types/HospitalListItem'
export type { HospitalNested } from '../generated/types/HospitalNested'
export type { PaginatedHospitalListItemList } from '../generated/types/PaginatedHospitalListItemList'

// ── Typed adapters for legacy component props ───────────────────────

/**
 * List hospitals shaped for `DECommonTable.apiGetListAction`.
 *
 * The table component is generic over `HospitalChainProxy` (the legacy
 * model the column definitions are typed against). At runtime the
 * generated `HospitalListItem` has the same fields the columns read, so
 * the cast is a type-only bridge — encapsulated here so call sites stay
 * clean.
 */
export async function listHospitalsForTable(
  params: ListHospitalsQueryParams = {}
): Promise<StandardPaginatedResponse<HospitalChainProxy>> {
  return listHospitalsClient(
    params
  ) as unknown as StandardPaginatedResponse<HospitalChainProxy>
}

/**
 * Get the hospitals summary count shaped for
 * `DECommonTable.apiGetSummaryAction`.
 *
 * The legacy `StandardSummaryResponse` carries a second `alt_count` field the
 * hospitals backend never populates (only the doors summary uses it), so we
 * build the response explicitly and default `alt_count` to 0 — the same value
 * the shared table plumbing falls back to.
 */
export async function getHospitalsSummaryForTable(
  params: GetHospitalsSummaryQueryParams = {}
): Promise<StandardSummaryResponse> {
  const res = await getHospitalsSummaryClient(params)
  return { count: res.count, alt_count: 0 }
}

/**
 * Convert the generated `HospitalNested.address_info` (a `VerticalZipCode`
 * shape) into the legacy `AddressInfo` interface that `DemographicsSection`
 * accepts.
 *
 * `models/restaurant.AddressInfo` declares every demographic field as
 * required & non-null; the generated `VerticalZipCode` correctly types them
 * as optional. `DemographicsSection` already defends against missing fields,
 * so the runtime data is compatible. This helper makes the type bridge
 * explicit instead of forcing every call site to repeat the cast.
 */
export function toLegacyAddressInfo(
  addressInfo: HospitalNested['address_info'] | null | undefined
): AddressInfo | undefined {
  if (!addressInfo) return undefined
  return addressInfo as AddressInfo
}
